import { useState, useEffect, useRef, type ReactNode } from 'react'
import { Bell, LogOut, Menu, X, CheckCheck, Wifi, WifiOff, LoaderCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { ThemeToggle } from '@/components/project/ThemeToggle'
import { MediaAvatar } from '@/components/ui/media-avatar'
import { useAuth } from '@/modules/auth/AuthContext'
import { cn } from '@/lib/utils'
import { getNotifications, markNotificationRead, markAllNotificationsRead, type NotificationItem } from '@/api/http/notification-api'
import { useNotificationSocket, type NotificationSocketStatus } from '@/modules/notifications/useNotificationSocket'

interface TopbarProps {
  onMenuClick: () => void
  onMobileMenuClick: () => void
  sidebarCollapsed: boolean
  onLogout: () => void
}

export function Topbar({ onMenuClick, onMobileMenuClick, sidebarCollapsed, onLogout }: TopbarProps) {
  const { state } = useAuth()
  const user = state.status === 'authenticated' ? state.user : null
  const [notifications, setNotifications] = useState<NotificationItem[]>([])
  const [showDropdown, setShowDropdown] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const { unreadCount, setUnreadCount, status } = useNotificationSocket(state.status === 'authenticated')

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleBellClick = async () => {
    if (showDropdown) {
      setShowDropdown(false)
      return
    }
    const recent = await getNotifications(1, 10)
    setNotifications(recent.items ?? [])
    setShowDropdown(true)
  }

  const handleMarkRead = async (id: number) => {
    await markNotificationRead(id)
    setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)))
    setUnreadCount((prev) => Math.max(0, prev - 1))
  }

  const handleMarkAllRead = async () => {
    await markAllNotificationsRead()
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })))
    setUnreadCount(0)
  }

  const socketMeta: Record<NotificationSocketStatus, { label: string; tone: string; icon: ReactNode }> = {
    connected: {
      label: 'Realtime подключён',
      tone: 'text-emerald-600 dark:text-emerald-400',
      icon: <Wifi className="w-3.5 h-3.5" strokeWidth={2} />,
    },
    connecting: {
      label: 'Подключение realtime',
      tone: 'text-text-muted',
      icon: <LoaderCircle className="w-3.5 h-3.5 animate-spin" strokeWidth={2} />,
    },
    reconnecting: {
      label: 'Realtime переподключается',
      tone: 'text-amber-600 dark:text-amber-400',
      icon: <LoaderCircle className="w-3.5 h-3.5 animate-spin" strokeWidth={2} />,
    },
    offline: {
      label: 'Realtime недоступен',
      tone: 'text-red',
      icon: <WifiOff className="w-3.5 h-3.5" strokeWidth={2} />,
    },
  }
  const currentSocketMeta = socketMeta[status]

  return (
    <header className="flex items-center gap-2 h-13 px-4 border-b border-border bg-surface shrink-0">
      <Button
        variant="ghost"
        size="icon"
        className="hidden md:inline-flex"
        onClick={onMenuClick}
        aria-label={sidebarCollapsed ? 'Развернуть меню' : 'Свернуть меню'}
      >
        <Menu className="w-4 h-4" strokeWidth={2} />
      </Button>

      <Button
        variant="ghost"
        size="icon"
        className="md:hidden"
        onClick={onMobileMenuClick}
        aria-label="Открыть меню"
      >
        <Menu className="w-4 h-4" strokeWidth={2} />
      </Button>

      <div className="flex items-center gap-1 ml-auto" title={currentSocketMeta.label}>
        <div data-testid="notification-socket-status" className={currentSocketMeta.tone}>
          {currentSocketMeta.icon}
        </div>
        <MediaAvatar photoUrl={user?.avatar_url} label={user?.full_name || user?.email || '?'} size="default" />
        <ThemeToggle />
        <div className="relative" ref={dropdownRef}>
          <Button variant="ghost" size="icon" aria-label="Уведомления" onClick={handleBellClick}>
            <Bell className="w-4 h-4" strokeWidth={2} />
            {unreadCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center w-4 h-4 rounded-full bg-red text-[10px] font-semibold text-white">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </Button>
          {showDropdown && (
            <div className="absolute right-0 top-full mt-1 w-80 rounded-lg border border-border bg-surface shadow-lg z-50">
              <div className="flex items-center justify-between px-3 py-2 border-b border-border">
                <span className="text-xs font-medium text-text-muted">Уведомления</span>
                {unreadCount > 0 && (
                  <button
                    onClick={handleMarkAllRead}
                    className="flex items-center gap-1 text-xs text-accent hover:text-accent/70 transition-colors"
                  >
                    <CheckCheck className="w-3.5 h-3.5" strokeWidth={2} />
                    Прочитать все
                  </button>
                )}
              </div>
              <div className="max-h-80 overflow-y-auto">
                {notifications.length === 0 ? (
                  <p className="text-sm text-text-muted text-center py-6">Нет уведомлений</p>
                ) : (
                  notifications.map((n) => (
                    <div
                      key={n.id}
                      className={cn(
                        'flex items-start gap-2.5 px-3 py-2.5 border-b border-border/50 last:border-b-0 transition-colors',
                        !n.read && 'bg-accent/5',
                      )}
                    >
                      <div className="flex-1 min-w-0">
                        <p className={cn('text-sm', !n.read ? 'font-medium text-text' : 'text-text-muted')}>
                          {n.title}
                        </p>
                        <p className="text-xs text-text-muted mt-0.5 line-clamp-2">{n.body}</p>
                      </div>
                      {!n.read && (
                        <button
                          onClick={() => handleMarkRead(n.id)}
                          className="shrink-0 p-1 text-text-muted hover:text-text transition-colors"
                          title="Отметить прочитанным"
                        >
                          <X className="w-3.5 h-3.5" strokeWidth={2} />
                        </button>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
        <div className="w-px h-5 bg-border mx-1" />
        <Button variant="ghost" size="sm" className="text-accent gap-1.5" onClick={onLogout}>
          <LogOut className="w-4 h-4" strokeWidth={2} />
          <span className="hidden sm:inline">Выйти</span>
        </Button>
      </div>
    </header>
  )
}
